import { Platform, Pressable, PressableProps, StyleSheet } from 'react-native';

import { tokens } from '../../tokens';
import { IconAdd, IconRemove } from '../icon';

export type QuantityIconNames = 'ADD' | 'REMOVE';

export type QuantityPressableProps = Omit<
  PressableProps,
  'role' | 'aria-checked' | 'aria-label' | 'onPress' | 'disabled'
>;

type IQuantityStepperButtonProp = QuantityPressableProps & {
  iconName: QuantityIconNames;
  disabled?: boolean;
  softDisabled?: boolean;
  handlePress: () => void;
  handleSoftDisabledPress?: () => void;
  accessibilityLabel?: string;
};

const iconSelector = (
  iconName: QuantityIconNames,
  disabled: boolean = false,
  softDisabled: boolean = false
) => {
  if (iconName === 'REMOVE') {
    return (
      <IconRemove
        accessible={false}
        aria-hidden
        color={
          disabled || softDisabled ? tokens.colors.$blackOpacity10 : tokens.colors.$houseRed
        }
        size={16}
      />
    );
  }

  if (iconName === 'ADD') {
    return (
      <IconAdd
        accessible={false}
        aria-hidden
        color={
          disabled || softDisabled ? tokens.colors.$blackOpacity10 : tokens.colors.$houseRed
        }
        size={16}
      />
    );
  }

  if (__DEV__) {
    throw new Error('Must add a valid icon name. Type: QuantityIconNames');
  }

  return null;
};

export const QuantityStepperButton = ({
  disabled,
  softDisabled,
  iconName,
  handlePress,
  handleSoftDisabledPress,
  accessibilityLabel,
  ...props
}: IQuantityStepperButtonProp) => (
  <Pressable
    role="button"
    disabled={disabled}
    onPress={softDisabled ? handleSoftDisabledPress : handlePress}
    accessibilityLabel={accessibilityLabel}
    style={[styles.container, disabled && styles.disabled, softDisabled && styles.softDisabled]}
    {...props}
  >
    {iconSelector(iconName, disabled, softDisabled)}
  </Pressable>
);

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
    width: 34,
    height: 34,
    padding: 8,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: tokens.colors.$houseRed,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  disabled: {
    borderColor: tokens.colors.$blackOpacity10,
  },
  softDisabled: {
    borderColor: tokens.colors.$blackOpacity10,
  },
});
